import { useState, useEffect, useCallback } from 'react';
import { appLogger } from '../../../../utils/errorLogger';
import * as clientApi from '../../../../api/clients';
import * as propertyApi from '../../../../api/properties';
import * as supplierApi from '../../../../api/suppliers';
import * as subServiceApi from '../../../../api/subServices';
import * as lookupApi from '../../../../api/lookups';
import { LookupType } from '../../../../types/lookup.types';
import { Client } from '../../../../types/client';
import { Property } from '../../../../types/property.types';
import { UtilityType, Supplier, SubService } from '../../../../types/utility.types';
import toast from 'react-hot-toast';

interface UseContractDataReturn {
  clients: Client[];
  properties: Property[];
  suppliers: Supplier[];
  serviceTypes: UtilityType[];
  statuses: string[];
  availableSubServices: SubService[];
  selectedClient: Client | null;
  loading: boolean;
  error: string | null;
  loadClient: (id: string) => Promise<void>;
  loadProperties: (clientId: string) => Promise<void>;
  setSelectedClient: (client: Client | null) => void;
  setProperties: (properties: Property[]) => void;
}

export const useContractData = (): UseContractDataReturn => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [serviceTypes, setServiceTypes] = useState<UtilityType[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [availableSubServices, setAvailableSubServices] = useState<SubService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load client details
  const loadClient = useCallback(async (id: string) => {
    try {
      const client = await clientApi.getClient(id);
      setSelectedClient(client);
    } catch (err) {
      console.error('Failed to load client:', err);
      appLogger.error(err instanceof Error ? err : new Error('Failed to load client'), {
        metadata: { clientId: id },
        source: 'useContractData.loadClient'
      });
      toast.error('Failed to load client details');
    }
  }, []);

  // Load properties for a client
  const loadProperties = useCallback(async (clientId: string) => {
    if (!clientId) {
      setProperties([]);
      return;
    }
    
    try {
      const fetchedProperties = await propertyApi.getClientProperties(clientId);
      setProperties(fetchedProperties || []);
    } catch (err) {
      console.error('Failed to load properties:', err);
      appLogger.error(err instanceof Error ? err : new Error('Failed to load properties'), {
        metadata: { clientId },
        source: 'useContractData.loadProperties'
      });
      toast.error('Failed to load properties for this client');
    }
  }, []);

  // Load initial data (clients, suppliers, service types, statuses)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load clients
        const fetchedClients = await clientApi.getClients();
        setClients(fetchedClients || []);
        
        // Load suppliers
        const fetchedSuppliers = await supplierApi.fetchSuppliers();
        setSuppliers(fetchedSuppliers || []);
        
        // Load service types and statuses from lookups - no fallbacks
        try {
          const lookups = await lookupApi.getLookupsByType('ServiceType' as LookupType);
          
          if (lookups && lookups.length > 0) {
            const types = lookups.map(lookup => lookup.value as UtilityType);
            setServiceTypes(types);
          } else {
            console.warn('No service type lookups found in AdminPanel - contract creation will be disabled');
            setServiceTypes([]);
          }

          const statusLookups = await lookupApi.getLookupsByType('UtilityServiceStatus' as LookupType);
          
          if (statusLookups && statusLookups.length > 0) {
            const statuses = statusLookups.map(lookup => lookup.value as string);
            setStatuses(statuses);
          } else {
            console.warn('No status lookups found in AdminPanel');
            setStatuses([]);
          }
        } catch (err) {
          console.error('Failed to load lookups:', err);
          appLogger.error(err instanceof Error ? err : new Error('Failed to load lookups'), {
            source: 'useContractData.loadInitialData'
          });
          // No fallbacks - empty arrays will disable contract creation
          setServiceTypes([]);
          setStatuses([]);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
        appLogger.error(err instanceof Error ? err : new Error('Failed to load initial data'), {
          source: 'useContractData.loadInitialData'
        });
        setError('Failed to load initial data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  return {
    clients,
    properties,
    suppliers,
    serviceTypes,
    statuses,
    availableSubServices,
    selectedClient,
    loading,
    error,
    loadClient,
    loadProperties,
    setSelectedClient,
    setProperties,
  };
};
